/**
 * **The authoring-lint local rules** — a pure,
 * read-only static check over one clause/entry that makes a structurally-dead or wrongly-firing
 * authored clause **loud at authoring time** instead of silent at runtime. Lives beside
 * `coverage.ts`/`inertTriggers.ts` (same package, same "read-only static analysis over the
 * overlay" family) rather than in the CLI's authoring dir — the bound-roles table this composes
 * with (`coverage.ts`'s {@link FAMILY_BOUND_ROLES}/{@link ACTIVATE_BOUND_ROLES}) must stay in
 * lock-step with the fire sites, and this module reads it directly rather than re-deriving the
 * family list.
 *
 * **Local rules only** — every rule here is answerable from ONE clause alone, so it is safe
 * to run in an authoring agent's fast per-entry loop as well as at package time (the global
 * dead-status-read rule needs the whole corpus and is wired at the CLI/package layer, not
 * here). `lintEntryLocal`/`lintCorpusLocal` are the two call shapes: one authored entry (the
 * fast mode) or a whole overlay corpus (the package-time mode) — same pure function either way,
 * and printing the honesty line is the CLI's job, not this module's.
 *
 * **Composes with, never re-implements, the two existing carrier-level checks**
 * (`coverage.ts`'s `REACHABLE_TRIGGER_POINTS`-driven demotion, `inertTriggers.ts`'s
 * `inertTriggeredClauses`) — this module's rules are about a clause's *authored actor refs and
 * atom shapes*, not carrier-kind reachability or trigger-family liveness, which those two already
 * cover. A caller composes all three; this module doesn't duplicate either.
 *
 * **The severity contract:** `error` means the engine's own resolution proves the clause dead
 * or structurally unreachable (the removed `attacker` fallback, the `EffectContext.actors`
 * closed set, the system-modifier attack-fold gap); `warn` means the clause resolves to
 * something REAL but almost certainly not what the author meant (a silent alias, a complement
 * encoding). Every rule reads its verdict off a calibration table
 * (`FAMILY_BOUND_ROLES`/`ACTIVATE_BOUND_ROLES`) or a traced fire-site fact — never a
 * hand-listed family set, which is exactly how the `save`-family bug got introduced once already.
 */

import type {
  EffectAtom,
  EffectEntry,
  ModifierContribution,
  OverlayEntry,
  TriggeredEffect,
} from "./atoms";
import { predicateActorRefFields, type Predicate } from "./predicate";
import {
  ACTIVATE_BOUND_ROLES,
  FAMILY_BOUND_ROLES,
  OBSERVED_MOVE_BOUND_ROLES,
  REACHABLE_MODIFIER_FACETS,
  reachabilityKindOf,
  type BoundRoleProfile,
} from "./coverage";
import { INTRINSIC_STATUS_NAMES } from "./sidecar";

// ── the finding shape ──────────────────────────────────────────────────────────────

/** A lint finding's severity — `error` (provably dead/unreachable) or `warn` (live but almost certainly not what was meant). */
export type LintSeverity = "error" | "warn";

/**
 * One local-rule finding. `entry`/`clause` locate the authored clause (mirrors
 * `ClauseCoverage`'s own addressing); `locator` is the field path WITHIN the clause (e.g.
 * `condition.who`, `effect[2].on_fail[0].target`) — the exact site a reviewer/author fixes.
 */
export type LintFinding = {
  /** The overlay entry id this finding belongs to. */
  entry: string;
  /** The clause's index within the entry's `effects[]` (stable address, mirrors `ClauseCoverage.clause`). */
  clause: number;
  /** A stable, kebab-case rule id (for filtering/regression fixtures). */
  rule: string;
  severity: LintSeverity;
  /** The field path within the clause the finding anchors to. */
  locator: string;
  /** Human-readable explanation — states the WHY, never just the WHAT. */
  message: string;
};

function finding(args: {
  entry: string;
  clause: number;
  rule: string;
  severity: LintSeverity;
  locator: string;
  message: string;
}): LintFinding {
  return args;
}

// ── shared clause accessors (discriminated-union narrowing in one place) ──────────────

/** The clause's `condition` predicate (an invade option's `when`), or `undefined` for a `select` clause (which carries none). */
function clauseCondition(clause: EffectEntry): Predicate | undefined {
  switch (clause.kind) {
    case "modifier":
    case "triggered":
    case "action":
      return clause.condition;
    case "invade_option":
      return clause.when;
    case "replacement":
      return clause.on === "roll" ? clause.condition : undefined;
    case "select":
      return undefined;
  }
}

/** The clause's atom-list `effect` — only `triggered`/`action`/`invade_option` clauses carry one. */
function clauseEffectAtoms(clause: EffectEntry): readonly EffectAtom[] {
  switch (clause.kind) {
    case "triggered":
    case "action":
    case "invade_option":
      return clause.effect;
    case "modifier":
    case "select":
    case "replacement":
      return [];
  }
}

// ── generic tree walkers (shared by several rules below) ──────────────────────────────

/** Recurse a predicate tree, invoking `visit` on every node (composites AND leaves) with its field path. */
function walkPredicateTree(pred: Predicate, path: string, visit: (node: Predicate, path: string) => void): void {
  visit(pred, path);
  if (pred.op === "and" || pred.op === "or") {
    pred.preds.forEach((p, i) => walkPredicateTree(p, `${path}.preds[${i}]`, visit));
  } else if (pred.op === "not") {
    walkPredicateTree(pred.pred, `${path}.pred`, visit);
  }
}

/**
 * Recurse an atom list, invoking `atomVisit` on every atom (including nested control-flow
 * bodies) and `predVisit` on every predicate reachable from an `if` atom's `cond` or a
 * `for_each` atom's `in.where` — mirrors `coverage.ts`'s `collectAtomOps`/`collectPredicateOps`
 * recursion shape (force_save on_fail/on_pass, roll on_hit/on_miss, if then/else, choose
 * options, for_each effect), generalized to visit every node rather than just collect op names.
 */
type AtomVisitors = {
  atom: (atom: EffectAtom, path: string) => void;
  pred: (node: Predicate, path: string) => void;
};

function walkAtoms(atoms: readonly EffectAtom[], path: string, visitors: AtomVisitors): void {
  atoms.forEach((a, i) => {
    const p = `${path}[${i}]`;
    visitors.atom(a, p);
    switch (a.op) {
      case "force_save":
      case "skill_check":
      case "deploy_mine":
        if (a.on_fail) walkAtoms(a.on_fail, `${p}.on_fail`, visitors);
        if (a.on_pass) walkAtoms(a.on_pass, `${p}.on_pass`, visitors);
        break;
      case "roll":
        if (a.on_hit) walkAtoms(a.on_hit, `${p}.on_hit`, visitors);
        if (a.on_miss) walkAtoms(a.on_miss, `${p}.on_miss`, visitors);
        break;
      case "contested_roll":
        if (a.on_win) walkAtoms(a.on_win, `${p}.on_win`, visitors);
        if (a.on_lose) walkAtoms(a.on_lose, `${p}.on_lose`, visitors);
        break;
      case "if":
        walkPredicateTree(a.cond, `${p}.cond`, visitors.pred);
        walkAtoms(a.then, `${p}.then`, visitors);
        if (a.else) walkAtoms(a.else, `${p}.else`, visitors);
        break;
      case "choose":
        a.options.forEach((o, oi) => walkAtoms(o.effect, `${p}.options[${oi}].effect`, visitors));
        break;
      case "for_each":
      case "choose_random":
      case "choose_from_area":
        if (a.in.where) walkPredicateTree(a.in.where, `${p}.in.where`, visitors.pred);
        walkAtoms(a.effect, `${p}.effect`, visitors);
        break;
      default:
        break;
    }
  });
}

/**
 * One authored actor-ref site: the literal ref string (`"self"`/`"target"`/`"attacker"`/`"partner"`/a raw id) and where it was found.
 * `rebound` marks a site inside an area atom's body or `in.where`, where `target` is the enumerated
 * member, not the family's binding.
 */
type ActorRefSite = { ref: string; path: string; rebound: boolean };

/** Extract the `AtomActorRef`-typed fields off ONE atom node (non-recursive — nested bodies are walked separately). */
function atomActorRefs(node: EffectAtom): { field: string; ref: string }[] {
  const out: { field: string; ref: string }[] = [];
  const magnitudeWho = (v: unknown): void => {
    if (v && typeof v === "object" && "who" in v && typeof (v as { who?: unknown }).who === "string") {
      out.push({ field: "amount.who", ref: (v as { who: string }).who });
    }
  };
  switch (node.op) {
    case "add_status":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      if (node.partner !== undefined) out.push({ field: "partner", ref: node.partner });
      break;
    case "remove_status":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      if (node.exceptSource !== undefined) out.push({ field: "exceptSource", ref: node.exceptSource });
      break;
    case "pause_status_expiry":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      break;
    case "add_resource":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      break;
    case "set_resource":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      break;
    case "grant_action_allowance":
      if (node.who !== undefined) out.push({ field: "who", ref: node.who });
      break;
    case "extra_damage":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      // `amount` is a `MagnitudeValue` now (the `{TARGET_COUNT}`/tier/stat widening), so its
      // optional `who` is a real actor ref the unbound-ref lint must see — same as `add_heat`/
      // `heal` below.
      magnitudeWho(node.amount);
      break;
    case "add_heat":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      magnitudeWho(node.amount);
      break;
    case "set_hp":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      break;
    case "overshield":
    case "clear_burn":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      break;
    case "grant_move":
      if (node.who !== undefined) out.push({ field: "who", ref: node.who });
      magnitudeWho(node.cells);
      break;
    case "reveal":
    case "despawn":
    case "move_up_to":
    case "reload":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      break;
    case "heal":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      magnitudeWho(node.amount);
      break;
    case "forced_move":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      if (node.away_from !== undefined && node.away_from !== "site") out.push({ field: "away_from", ref: node.away_from });
      break;
    case "move_self_toward_target":
      if (node.who !== undefined) out.push({ field: "who", ref: node.who });
      if (node.toward !== undefined && node.toward !== "chosen") out.push({ field: "toward", ref: node.toward });
      if (node.chooser !== undefined) out.push({ field: "chooser", ref: node.chooser });
      break;
    case "deploy_object":
      if (node.near !== undefined) out.push({ field: "near", ref: node.near });
      break;
    case "deploy_mine":
      if (node.near !== undefined) out.push({ field: "near", ref: node.near });
      break;
    case "recall_object":
    case "redeploy_object":
      if (node.near !== undefined) out.push({ field: "near", ref: node.near });
      break;
    case "repair_object_group":
      if (node.near !== undefined) out.push({ field: "near", ref: node.near });
      break;
    case "force_save":
      if (node.actor !== undefined) out.push({ field: "actor", ref: node.actor });
      break;
    case "for_each":
    case "choose_random":
    case "choose_from_area":
      if (node.in.origin === "target") out.push({ field: "in.origin", ref: "target" });
      break;
    case "place_adjacent":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      if (node.to !== undefined) out.push({ field: "to", ref: node.to });
      break;
    case "destroy_system":
      if (node.who !== undefined) out.push({ field: "who", ref: node.who });
      break;
    case "move_spaces":
      if (node.who !== undefined) out.push({ field: "who", ref: node.who });
      magnitudeWho(node.dist);
      break;
    case "teleport":
      if (node.who !== undefined) out.push({ field: "who", ref: node.who });
      if (node.swap !== undefined) {
        out.push({ field: "swap", ref: node.swap });
        break;
      }
      if (node.from !== undefined && node.from !== "site") out.push({ field: "from", ref: node.from });
      if (node.chooser !== undefined) out.push({ field: "chooser", ref: node.chooser });
      magnitudeWho(node.dist);
      break;
    case "forced_attack":
      if (node.target !== undefined) out.push({ field: "target", ref: node.target });
      if (node.attacker !== undefined) out.push({ field: "attacker", ref: node.attacker });
      break;
    case "create_area":
      // Mirrors `for_each`'s own `in.origin` handling just above (same symbolic vocabulary,
      // `"site"` is a keyword not an actor ref) — only "target" is interesting to the
      // attacker/target bound-role rules below; "self" is never flagged by any rule.
      if (node.origin === "target") out.push({ field: "origin", ref: "target" });
      if (node.source !== undefined) out.push({ field: "source", ref: node.source });
      break;
    case "contested_roll":
      out.push({ field: "vs", ref: node.vs });
      break;
    case "reserve_followup":
    case "remove_status_from_source":
    case "reduce_pending_damage":
    case "split_pending_damage":
    case "redirect_pending_damage":
    case "resist_pending_damage":
    case "cause_miss":
    case "roll":
    case "choose":
    case "builtin":
      break;
  }
  return out;
}

/** Collect every authored actor-ref site (predicate `who`/`relativeTo` + atom `target`/`who`/`actor`/`near`/`away_from`/`toward`/`partner`) in a clause. */
function collectActorRefSites(clause: EffectEntry): ActorRefSite[] {
  const out: ActorRefSite[] = [];
  const cond = clauseCondition(clause);
  if (cond) {
    walkPredicateTree(cond, "condition", (node, path) => {
      for (const { field, ref } of predicateActorRefFields(node)) out.push({ ref, path: `${path}.${field}`, rebound: false });
    });
  }
  // Parents are visited before their bodies, so an area atom's path is known by the time its body's sites are.
  // A `deploy_mine`'s clauses run at detonation with `target` bound to each caught character.
  const areaPaths: string[] = [];
  const minePaths: string[] = [];
  const rebound = (path: string): boolean =>
    areaPaths.some((p) => path.startsWith(`${p}.effect`) || path.startsWith(`${p}.in.where`)) ||
    minePaths.some((p) => path.startsWith(`${p}.on_fail`) || path.startsWith(`${p}.on_pass`));
  walkAtoms(clauseEffectAtoms(clause), "effect", {
    atom: (node, path) => {
      for (const { field, ref } of atomActorRefs(node)) out.push({ ref, path: `${path}.${field}`, rebound: rebound(path) });
      if (node.op === "for_each" || node.op === "choose_random" || node.op === "choose_from_area") areaPaths.push(path);
      if (node.op === "deploy_mine") minePaths.push(path);
    },
    pred: (node, path) => {
      for (const { field, ref } of predicateActorRefFields(node)) out.push({ ref, path: `${path}.${field}`, rebound: rebound(path) });
    },
  });
  return out;
}

/** Collect every `has_status` with `source: "carrier"` predicate node's path in a clause. */
function collectCarrierSourceSites(clause: EffectEntry): string[] {
  const out: string[] = [];
  const visit = (node: Predicate, path: string): void => {
    if (node.op === "has_status" && node.source === "carrier") out.push(path);
  };
  const cond = clauseCondition(clause);
  if (cond) walkPredicateTree(cond, "condition", visit);
  walkAtoms(clauseEffectAtoms(clause), "effect", { atom: () => {}, pred: visit });
  return out;
}

/** Collect every atom node anywhere in a clause (recursive) whose `op` is in `ops`, with its path. */
function collectAtomsByOp(clause: EffectEntry, ops: ReadonlySet<EffectAtom["op"]>): { op: string; path: string }[] {
  const out: { op: string; path: string }[] = [];
  walkAtoms(clauseEffectAtoms(clause), "effect", {
    atom: (node, path) => {
      if (ops.has(node.op)) out.push({ op: node.op, path });
    },
    pred: () => {},
  });
  return out;
}

// ── rule 1+2: attacker/target bound-role rules (the calibration table) ────────────────

/**
 * Rules 1+2 of the v1 catalog: an `attacker`/`target` ref, read off {@link FAMILY_BOUND_ROLES}
 * (a `triggered` clause, keyed by `clause.on`) or {@link ACTIVATE_BOUND_ROLES} (an `action`-kind
 * clause, no family). **Deliberately does NOT hand-list families** — `save` genuinely binds
 * `target`, and hand-listing "every family but attack" is
 * exactly the bug that produced. `modifier`/`select` clauses carry no `on`/activation profile at
 * all — this rule is silent on them (their `attacker`/`target` binding comes from whichever fold
 * consumes them — the attack fold or `interpretForceSaveRoll` — both of which bind real ids).
 */
function ruleActorBinding(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (clause.kind !== "triggered" && clause.kind !== "action") return [];
  const profile: BoundRoleProfile =
    clause.kind === "action" ? ACTIVATE_BOUND_ROLES : clause.on === "move" && isBystanderOffer(clause) ? OBSERVED_MOVE_BOUND_ROLES : FAMILY_BOUND_ROLES[clause.on];
  const where = clause.kind === "triggered" ? `family "${clause.on}"` : "an action clause";
  const out: LintFinding[] = [];
  for (const { ref, path, rebound } of collectActorRefSites(clause)) {
    if (ref === "attacker") {
      if (profile.attacker === "unbound") {
        out.push(
          finding({
            entry: entryId,
            clause: clauseIdx,
            rule: "attacker-unbound",
            severity: "error",
            locator: path,
            message: `\`attacker\` at ${path} resolves to undefined at ${where} — post-D1 there is no \`self\` fallback. Say \`self\` if that's the intent, or file a vocabulary-demand.`,
          }),
        );
      } else if (profile.attacker === "aliased_carrier") {
        out.push(
          finding({
            entry: entryId,
            clause: clauseIdx,
            rule: "attacker-aliased",
            severity: "warn",
            locator: path,
            message: `\`attacker\` at ${path} resolves to the activator (the SAME id as \`self\`) at ${where}, not a distinct attacker — say \`self\` if that's the intent.`,
          }),
        );
      }
    } else if (ref === "target" && !rebound) {
      if (profile.target === "aliased_carrier") {
        out.push(
          finding({
            entry: entryId,
            clause: clauseIdx,
            rule: "target-aliased",
            severity: "warn",
            locator: path,
            message: `\`target\` at ${path} resolves to the carrier at ${where} — say \`self\` if that's the intent.`,
          }),
        );
      }
    }
  }
  return out;
}

// ── rule 3: partner outside a modifier clause ──────────────────────────────────────────

/**
 * Rule 3: a `partner` ref is `"unbound"` everywhere `EffectContext.actors` is the binding
 * surface (`interpret.ts` — no `partner` field on that type). Only a `modifier` clause's
 * `condition` evaluates against a `PredicateContext` `foldModifiers`/`attackPredicateContext`
 * populate (which CAN carry `partner`) — every other clause kind is skipped here. The atoms that
 * bind it are a `choose_from_area` or `for_each` with `as: "partner"`, so a ref inside its body is bound.
 */
function rulePartner(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (clause.kind === "modifier") return [];
  const bodies: string[] = [];
  walkAtoms(clauseEffectAtoms(clause), "effect", {
    atom: (node, path) => {
      if ((node.op === "choose_from_area" || node.op === "for_each") && node.as === "partner") bodies.push(`${path}.effect`);
    },
    pred: () => {},
  });
  const out: LintFinding[] = [];
  for (const { ref, path } of collectActorRefSites(clause)) {
    if (ref === "partner" && !bodies.some((b) => path.startsWith(b))) {
      out.push(
        finding({
          entry: entryId,
          clause: clauseIdx,
          rule: "partner-unbound",
          severity: "error",
          locator: path,
          message: `\`partner\` at ${path} is unbound on a "${clause.kind}" clause — \`EffectContext.actors\` carries no \`partner\` field outside a modifier's condition. File a vocabulary-demand if this clause genuinely needs a third-party bind.`,
        }),
      );
    }
  }
  return out;
}

// ── rule 4: has_status source:carrier outside a carrierSource-threading path ─────────────

/**
 * Rule 4, traced against the ACTUAL code. `carrierSource` is threaded onto the fired
 * `PredicateContext` at exactly two call sites:
 *
 *   - `reactionRegistry.ts`'s `compileAutomaticOutcome` — a `triggered` clause with
 *     `on: "damage"`, `optional !== true`, `protector !== true` (the automatic, non-offer gather
 *     direction; `t_heavy_gunner:1`'s Covering Fire Watcher rides exactly this shape).
 *   - `checkpointRegistry.ts`'s `compileAuthoredMoveReactionOffers` — a `triggered` clause with
 *     `on: "move"`, `optional === true`.
 *
 * **`compileAuthoredOffers` is NOT one of them**, despite reading like a threading site: traced
 * against `reactionRegistry.ts` directly, it builds its
 * `PredicateContext`/`EffectContext` with `{self, target, attacker}` only; it never reads
 * `h.carrierSource`. Neither does `compileProtectorOffers` (`on:"damage"`/`optional:true`/
 * `protector:true`). So an `on:"damage"`/`optional:true` clause — offer OR protector-gathered —
 * is genuinely dead for this predicate today, even though it sits on the "damage-family reaction
 * path." This rule reads the real threading, not the broader prose.
 */
function ruleCarrierSource(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  const sites = collectCarrierSourceSites(clause);
  if (sites.length === 0) return [];
  const liveOnDamage = clause.kind === "triggered" && clause.on === "damage" && clause.optional !== true && clause.protector !== true;
  const liveOnMove = clause.kind === "triggered" && clause.on === "move" && clause.optional === true;
  if (liveOnDamage || liveOnMove) return [];
  const shape =
    clause.kind === "triggered"
      ? `kind="triggered", on="${clause.on}", optional=${String(clause.optional === true)}, protector=${String(clause.protector === true)}`
      : `kind="${clause.kind}"`;
  return sites.map((path) =>
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "carrier-source-unreachable",
      severity: "error",
      locator: path,
      message: `\`has_status\` with \`source: "carrier"\` at ${path} never threads \`carrierSource\` on this clause shape (${shape}) — it reads unconditionally false. Live only on a damage-family clause consumed by compileAutomaticOutcome (on:"damage", optional !== true, protector !== true) or a move-family offer consumed by compileAuthoredMoveReactionOffers (on:"move", optional: true).`,
    }),
  );
}

// ── rule 5: match.ranged === false complement encoding ─────────────────────────────────

/**
 * Rule 5: `match: { ranged: false }` silently includes tech attacks (a tech attack is also not-ranged) — `AUTHORING-PLAYBOOK.md`'s documented gotcha.
 * `{ ranged: false, tech: false }` names every melee attack (Grapple and Ram included) and is not flagged.
 */
function ruleRangedFalse(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  const match = clause.kind === "triggered" || clause.kind === "modifier" ? clause.match : undefined;
  if (!match || match.family !== "attack" || match.ranged !== false || match.tech === false) return [];
  return [
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "ranged-false-complement",
      severity: "warn",
      locator: "match.ranged",
      message: `match.ranged: false silently includes tech attacks (a tech attack is also not-ranged) — if "melee attack" is the intent, use match.weaponType: "Melee" instead (AUTHORING-PLAYBOOK.md's complement-encoding gotcha).`,
    }),
  ];
}

// ── rule 6: split_pending_damage / redirect_pending_damage / resist_pending_damage outside a damage clause ─

const PENDING_DAMAGE_OPS = new Set<EffectAtom["op"]>(["split_pending_damage", "redirect_pending_damage", "resist_pending_damage"]);

/**
 * Rule 6: these three atoms are reaction-window-only, recognized ONLY off a `damage`-family
 * clause — anywhere else, a silent advisory no-op. One exception: the declared window
 * (`declaredReactions.ts`) reads `resist_pending_damage` into the attack's Resistance, so a
 * declared-phase bystander offer may carry it. `resist_pending_damage`
 * (`npc-features-fielded-roster` Phase 7.1) joins the two pre-extracted siblings here even though
 * it is NOT pre-extracted (`atoms.ts`'s own doc) — the coarser "family === damage" check this rule
 * already makes is still the right bar: on a non-`damage` clause `ctx.pendingDamageWindow` is
 * never threaded either, so the atom is JUST as silently inert there.
 */
function rulePendingDamageAtoms(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  const isDamageTriggered = clause.kind === "triggered" && clause.on === "damage";
  if (isDamageTriggered) return rulePendingDamageAtBusSite(entryId, clauseIdx, clause);
  // The declared window also reads `resist_pending_damage` (Blinkfield Intervention).
  const ops = isDeclaredBystanderOffer(clause) ? new Set([...PENDING_DAMAGE_OPS].filter((op) => op !== "resist_pending_damage")) : PENDING_DAMAGE_OPS;
  const sites = collectAtomsByOp(clause, ops);
  const where = clause.kind === "triggered" ? `on="${clause.on}"` : `kind="${clause.kind}"`;
  return sites.map(({ op, path }) =>
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "pending-damage-atom-outside-damage",
      severity: "error",
      locator: path,
      message: `\`${op}\` at ${path} is a reaction-window-only atom recognized ONLY on a "damage"-family clause — here (${where}) it is a silent advisory no-op.`,
    }),
  );
}

/** All four pending-damage atoms — rule 6's set plus `reduce_pending_damage`, which is pre-extracted by its offer compiler. */
const ALL_PENDING_DAMAGE_OPS = new Set<EffectAtom["op"]>([...PENDING_DAMAGE_OPS, "reduce_pending_damage"]);

/**
 * Rule 6's extension (`decisions/0175`): on a `damage`-family clause the bus reaches — automatic,
 * so `optional !== true && protector !== true` — a pending-damage atom is honoured only at the
 * ATTACK site. Non-attack damage (a burn tick) fires the same clause through
 * `resolveDamageTrigger`, where no damage is pending and the atom is an advisory no-op. A `warn`,
 * not an `error`: the clause is correct for attacks, and Argonaut Shield ships exactly this shape.
 * Optional and protector clauses never reach the bus, so they are not flagged.
 */
function rulePendingDamageAtBusSite(entryId: string, clauseIdx: number, clause: TriggeredEffect): LintFinding[] {
  if (clause.optional === true || clause.protector === true) return [];
  return collectAtomsByOp(clause, ALL_PENDING_DAMAGE_OPS).map(({ op, path }) =>
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "pending-damage-atom-at-damage-bus",
      severity: "warn",
      locator: path,
      message: `\`${op}\` at ${path} is honoured only for ATTACK damage. On non-attack damage (burn, and the other emitters as they are wired) this automatic on="damage" clause fires on the trigger bus, where no damage is pending — there the atom is an advisory no-op.`,
    }),
  );
}

// ── rule 8: a {VAL} placeholder on a non-tg_ entry ──────────────────────────────────────

const VAL_PLACEHOLDER = /^\{VAL(?::[A-Za-z0-9_]+)?\}$/;

/** Pull the `effects[N]` clause index a generic walk's path string starts with, or `-1` if unparseable. */
function clauseIndexFromPath(path: string): number {
  const m = /^effects\[(\d+)\]/.exec(path);
  return m ? Number(m[1]) : -1;
}

/**
 * Rule 8: `{VAL}`/`{VAL:key}` only binds at load off a TAG's own `val` (`loader.ts`'s
 * `bindEntry`, called with `{ VAL: tag.val }`) — a placeholder authored on any other entry kind
 * (`ms_`/`mw_`/`t_`/`cb_`/`pg_`/a bare status/action name) never gets bound and loads as the
 * literal string forever. **Operates on the raw authored JSON shape**, not the strict runtime
 * `EffectEntry` types — a `{VAL}` string legitimately sits in a field the TS types declare
 * `number` (the authored/on-disk shape is a documented superset of the bound runtime type,
 * `schema.ts`'s own doc), so this walks the value generically rather than trusting the static
 * field types.
 */
function ruleValPlaceholder(entry: OverlayEntry): LintFinding[] {
  if (entry.id.startsWith("tg_")) return [];
  const hits: string[] = [];
  const walk = (v: unknown, path: string): void => {
    if (typeof v === "string") {
      if (VAL_PLACEHOLDER.test(v)) hits.push(path);
      return;
    }
    if (Array.isArray(v)) {
      v.forEach((x, i) => walk(x, `${path}[${i}]`));
      return;
    }
    if (v !== null && typeof v === "object") {
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) walk(val, `${path}.${k}`);
    }
  };
  entry.effects.forEach((clause, i) => walk(clause, `effects[${i}]`));
  return hits.map((path) =>
    finding({
      entry: entry.id,
      clause: clauseIndexFromPath(path),
      rule: "val-placeholder-non-tag",
      severity: "warn",
      locator: path,
      message: `{VAL} placeholder at ${path} on a non-tg_ entry — only tag templates get a bound value at load (loader.ts's bindEntry binds per-weapon off a tag's own \`val\`); this loads as the literal string forever.`,
    }),
  );
}

// ── rule 9: a modifier clause on a system carrier feeding the attack fold (defect C) ───

/**
 * Rule 9: a system-carried `modifier` clause authoring a facet outside {@link
 * REACHABLE_MODIFIER_FACETS}`.system` never reaches a real fold — structurally unreachable
 * — the real gather sites are `resolve/attackModifiers.ts`'s `atkMods`/`tgtMods`, `saves.ts`'s `save_ends`,
 * `contest.ts`'s opposed check, and `interpretForceSaveRoll`. This rule reads its allowlist off
 * the SAME shared table `coverage.ts`'s `classifyClause` reads, so widening the fold updates
 * both by construction and they cannot drift apart the way a private allowlist constant could.
 *
 * **`ms_stable_structure` is the explicit regression fixture** (see `lint.test.ts`): it authors
 * two system `modifier` clauses using ONLY `saveCheckBonus`/`saveScope` — both reachable — and
 * must NOT be flagged. An allowlist (rather than a hand-listed "attackerAccuracy and its
 * siblings" blocklist) is the safer shape here: any future `ModifierContribution` facet defaults
 * to flagged-unreachable-on-a-system-carrier unless someone deliberately widens
 * `gatherSystemModifiers`'s real consumers AND adds it to {@link REACHABLE_MODIFIER_FACETS}.
 */
function ruleSystemModifierAttackFold(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (clause.kind !== "modifier") return [];
  if (reachabilityKindOf(entryId) !== "system") return [];
  const offending = (Object.keys(clause.mod) as (keyof ModifierContribution)[]).filter(
    (k) => !REACHABLE_MODIFIER_FACETS.system.includes(k),
  );
  if (offending.length === 0) return [];
  return [
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "system-modifier-attack-fold-unreachable",
      severity: "error",
      locator: "mod",
      message: `modifier facet(s) ${offending.map((f) => `"${String(f)}"`).join(", ")} on a system carrier ("${entryId}") never reach a real fold — outside REACHABLE_MODIFIER_FACETS.system (coverage.ts). Structurally unreachable (defect C).`,
    }),
  ];
}

// ── rule 10: a requiresOptIn clause missing its answer key or its authored default, or on a family that cannot answer ──────

/**
 * Rule 10: the two fields a
 * `requiresOptIn` clause cannot be servable without, checked together because they share one
 * premise — the clause declares itself a player choice, so it owes both a key to record the
 * answer under and a stated initial position for the checkbox.
 *
 * - **`name`**: the answer is keyed `"optIn:" + name` (`triggerBus.ts`'s `fireTrigger`), so
 *   a clause missing `name` can never be answered — the checkbox has no key to record under, and
 *   the engine's fail-closed default (`atoms.ts`'s `TriggeredEffect.requiresOptIn` doc) makes
 *   it decline forever, silently.
 * - **`optInDefault`**: the authored initial checkbox state. Required rather than defaulted
 *   because an implicit default is exactly how an unconsidered value ships as a table-facing
 *   behaviour change — the honest answer differs per ability (Stormbringer's Prone is almost
 *   always wanted; a situational reposition is not), so neither a global on nor a global off is
 *   right, and the author must state which this one is. Note this governs the **UI pre-fill only**
 *   — the engine stays fail-closed either way, so a missing default is an authoring gap, not
 *   an engine hazard, and the severity below reflects the same structural-unreachability class
 *   only because an unauthored rider reaches the table with nobody having made the call.
 *
 * - **the family**: a clause on a status, move, trample, damage or turn/round lifecycle family
 *   can carry both fields and still never fire, because that family's fire site records no
 *   answer ({@link NO_CHOICE_FAMILY_FIRE_SITES}).
 *
 * All three are local (answerable from the one clause alone) and always `error`, the same severity
 * class as rule 1's `attacker-unbound`, not a style nit. They share this function — rather than
 * splitting into rules 10 and 11 — so the file keeps ONE clause-kind narrowing for the
 * `requiresOptIn` family.
 */
function ruleRequiresOptInServable(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (clause.kind !== "triggered" || clause.requiresOptIn !== true) return [];
  const out: LintFinding[] = [];
  if (!clause.name) {
    out.push(
      finding({
        entry: entryId,
        clause: clauseIdx,
        rule: "requires-optin-needs-name",
        severity: "error",
        locator: "name",
        message: `\`requiresOptIn: true\` at clause ${clauseIdx} has no \`name\` — the checkbox answer is keyed "optIn:" + name (triggerBus.ts), so an unnamed clause can never be answered and declines forever. Add a stable \`name\`.`,
      }),
    );
  }
  // `=== undefined`, NOT falsy: `optInDefault: false` is a real authored judgment (the checkbox
  // starts unticked) and must pass. A `!clause.optInDefault` test here would reject exactly the
  // deliberate default-off authoring this rule exists to make explicit.
  if (clause.optInDefault === undefined) {
    out.push(
      finding({
        entry: entryId,
        clause: clauseIdx,
        rule: "requires-optin-needs-default",
        severity: "error",
        locator: "optInDefault",
        message: `\`requiresOptIn: true\` at clause ${clauseIdx} has no \`optInDefault\` — the inspector's initial checkbox state would be an unstated fallback rather than an authored judgment (D9). State \`optInDefault: true\` (starts ticked — the rider is almost always wanted) or \`false\` (starts unticked — situational).`,
      }),
    );
  }
  const noChoiceSite = NO_CHOICE_FAMILY_FIRE_SITES.get(clause.on);
  if (noChoiceSite !== undefined) {
    out.push(
      finding({
        entry: entryId,
        clause: clauseIdx,
        rule: "requires-optin-unanswerable-family",
        severity: "error",
        locator: "requiresOptIn",
        message: `\`requiresOptIn: true\` at clause ${clauseIdx} fires on "${clause.on}", whose only fire site (resolve/triggers.ts's \`${noChoiceSite}\`) threads no choices — the fail-closed gate in triggerBus.ts declines it forever, so the clause never fires. Drop \`requiresOptIn\` (fire unconditionally, with an \`advisory\` if the table must judge) or move the clause to a family that records the player's answer.`,
      }),
    );
  }
  return out;
}

/**
 * The trigger families whose fire site passes no `ChoiceSource` to `fireTrigger`, keyed to that
 * fire site (all in `resolve/triggers.ts`). A `requiresOptIn` clause on any of them can never read
 * its `"optIn:" + name` answer and declines every time. The families that DO thread recorded
 * answers are `attack` (`resolve/attack.ts`, `resolve/attackTriggerFires.ts`), `action`
 * (`resolveActionTakenTrigger`) and `save` (`interpret.ts`, inheriting its enclosing
 * resolution's choices); `destruction` has no fire site at all and is the inert-trigger check's
 * to report. `heat` and `track_damage` fire from `referee/trackObserver.ts`, which threads no
 * choices either, but they are not listed here yet, so this rule does not catch a `requiresOptIn`
 * clause on them. Remove a family here only once its fire site threads recorded choices.
 */
const NO_CHOICE_FAMILY_FIRE_SITES: ReadonlyMap<string, string> = new Map([
  ["status", "resolveStatusLostHandlers"],
  ["move", "resolveMoveTrigger"],
  ["trample", "resolveTrampleTrigger"],
  ["damage", "resolveDamageTrigger"],
  ["aura", "resolveAuraTrigger"],
  ["start_turn", "resolveLifecycleTrigger"],
  ["end_turn", "resolveLifecycleTrigger"],
  ["start_round", "resolveLifecycleTrigger"],
  ["end_round", "resolveLifecycleTrigger"],
]);

// ── rule 11: frequency on a clause nothing reads it from ──────────────────────────────

/**
 * Rule 11: `frequency: "unlimited"` is read only by `compileAuthoredOffers` (`reactionRegistry.ts`),
 * which compiles `on:"damage"`, `optional: true`, non-protector clauses, and by
 * `compileBystanderOffers` (`toHitReactionRegistry.ts`, rule 13's shape). On any other clause it is
 * silently ignored and the reaction stays 1/round, so it is an error.
 */
function ruleFrequencyUnread(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (clause.kind !== "triggered" || clause.frequency === undefined) return [];
  if (clause.on === "damage" && clause.optional === true && clause.protector !== true) return [];
  if (isBystanderOffer(clause)) return [];
  return [
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "frequency-unread",
      severity: "error",
      locator: "frequency",
      message: `\`frequency\` at clause ${clauseIdx} is read only on a damage-window reaction offer (on:"damage", optional: true, no protector); on this clause it is ignored and the reaction keeps its 1/round cap.`,
    }),
  ];
}

// ── rule 13: bystander fields on a clause the bystander compiler never reads ───────────

/** The clause shape `compileBystanderOffers` (`toHitReactionRegistry.ts`) compiles, and its `move`-family twin the observed-move window (`observedMove.ts`) does. */
function isBystanderOffer(clause: EffectEntry): boolean {
  return clause.kind === "triggered" && (clause.on === "attack" || clause.on === "move") && clause.optional === true && clause.bystander === true;
}

/** A bystander offer the declared window (`declaredReactions.ts`) compiles: matched on `phase: "declared"`. */
function isDeclaredBystanderOffer(clause: EffectEntry): boolean {
  return isBystanderOffer(clause) && clause.kind === "triggered" && clause.match?.family === "attack" && clause.match.phase === "declared";
}

/**
 * Rule 13: `bystander` and `alsoSpends` are read only by `compileBystanderOffers`, which compiles
 * `on:"attack"`, `optional: true`, `bystander: true` clauses, and by the observed-move window, which
 * compiles the same shape on `on:"move"`. Elsewhere the clause is never offered to a bystander, or
 * its extra caps are never checked or spent, so either is an error.
 */
function ruleBystanderUnread(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (clause.kind !== "triggered" || isBystanderOffer(clause)) return [];
  const fields = (["bystander", "alsoSpends"] as const).filter((f) => clause[f] !== undefined);
  return fields.map((f) =>
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "bystander-unread",
      severity: "error",
      locator: f,
      message: `\`${f}\` at clause ${clauseIdx} is read only on a bystander offer (on:"attack" or on:"move", optional: true, bystander: true); on this clause it is ignored.`,
    }),
  );
}

// ── rule 14: cause_miss outside the declared window ───────────────────────────────────

/**
 * Rule 14: `cause_miss` does something only in the declared window (`declaredReactions.ts`), which
 * compiles a bystander offer matched on `phase: "declared"`. Anywhere else it is an advisory no-op
 * and the attack it names never misses, so it is an error. A to-hit reaction that turns a hit into
 * a miss uses the clause's `causesMiss` flag instead.
 */
function ruleCauseMissUnread(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (isDeclaredBystanderOffer(clause)) return [];
  return collectAtomsByOp(clause, new Set<EffectAtom["op"]>(["cause_miss"])).map(({ path }) =>
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "cause-miss-unread",
      severity: "error",
      locator: path,
      message: `\`cause_miss\` at ${path} is read only on a declared-window bystander offer (on:"attack", match.phase "declared", optional: true, bystander: true); here it is an advisory no-op.`,
    }),
  );
}

// ── rule 15: hostileActions outside the declared window ───────────────────────────────

/**
 * Rule 15: `match.hostileActions` is read only by the declared window's activation pass
 * (`activationDeclaredReactions`), which compiles declared-phase bystander offers. On any
 * other clause the clause never fires on an activation, so it is an error.
 */
function ruleHostileActionsUnread(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  if (clause.kind !== "triggered" || clause.match?.family !== "attack" || clause.match.hostileActions === undefined || isDeclaredBystanderOffer(clause)) return [];
  return [
    finding({
      entry: entryId,
      clause: clauseIdx,
      rule: "hostile-actions-unread",
      severity: "error",
      locator: "match.hostileActions",
      message: `\`match.hostileActions\` at clause ${clauseIdx} is read only on a declared-window bystander offer (on:"attack", match.phase "declared", optional: true, bystander: true); here the clause never fires on an activation.`,
    }),
  ];
}

// ── rule 12: action clause names, all or none ─────────────────────────────────────────

/**
 * Rule 12: an entry's action clauses are activated one by one, as `<entryId>#<name>`, only when
 * every one carries a distinct `name` (`actionIdsOf`, `loader.ts`). Name some and not others and
 * the entry falls back to the bare id, firing every clause at once; name two alike and the second
 * can never be reached. Either is an error. Runs once per entry.
 */
function ruleActionNames(entry: OverlayEntry): LintFinding[] {
  // A `usableBy` clause is always named (schema) and is not one of the carrier's own clauses.
  const actions = entry.effects.flatMap((clause, idx) => (clause.kind === "action" && clause.usableBy === undefined ? [{ idx, name: clause.name }] : []));
  const named = actions.filter((a) => a.name !== undefined);
  if (named.length === 0) return [];
  const partial = named.length < actions.length;
  const duplicate = new Set(named.map((a) => a.name)).size < named.length;
  if (!partial && !duplicate) return [];
  return [
    finding({
      entry: entry.id,
      clause: named[0]!.idx,
      rule: "action-name-partial",
      severity: "error",
      locator: "name",
      message: partial
        ? "some action clauses are named and some are not; name every action clause on the entry, or none, or activating it fires them all."
        : "two action clauses share a name; each needs its own, or only the first can be activated.",
    }),
  ];
}

// ── the entry point ─────────────────────────────────────────────────────────────────

/**
 * Every local rule, run per-clause (rules 1–6, 9–11). Rules 8 (`{VAL}` placement) and 12 (action
 * names) run once per entry — see {@link lintEntryLocal}. Rule 7 (the "unconditional `remove_status_from_source` +
 * `add_status` of the same status destroys instead of refreshes" check) was RETIRED in
 * — its premise is no longer true. `add_status` (`interpret.ts`) is step-aware: it recognizes an earlier atom in the SAME
 * effect list that already queued that exact target+status's removal, so the unconditional
 * pairing this rule used to flag is a correct, intended refresh shape (`t_exemplar:1`'s own
 * clause), not a bug. The number is retired rather than reused, mirroring how the other rule
 * numbers here are stable identifiers, not a dense sequence.
 */
function lintClauseLocal(entryId: string, clauseIdx: number, clause: EffectEntry): LintFinding[] {
  return [
    ...ruleActorBinding(entryId, clauseIdx, clause),
    ...rulePartner(entryId, clauseIdx, clause),
    ...ruleCarrierSource(entryId, clauseIdx, clause),
    ...ruleRangedFalse(entryId, clauseIdx, clause),
    ...rulePendingDamageAtoms(entryId, clauseIdx, clause),
    ...ruleSystemModifierAttackFold(entryId, clauseIdx, clause),
    ...ruleRequiresOptInServable(entryId, clauseIdx, clause),
    ...ruleFrequencyUnread(entryId, clauseIdx, clause),
    ...ruleBystanderUnread(entryId, clauseIdx, clause),
    ...ruleCauseMissUnread(entryId, clauseIdx, clause),
    ...ruleHostileActionsUnread(entryId, clauseIdx, clause),
  ];
}

/**
 * Run every v1 local rule over one authored overlay entry — the fast per-entry mode's core
 * (local rules run everywhere). Pure: reads only `entry` + the two hand-maintained
 * calibration tables (`coverage.ts`). No corpus, no state.
 */
export function lintEntryLocal(entry: OverlayEntry): LintFinding[] {
  const out: LintFinding[] = [];
  entry.effects.forEach((clause, i) => out.push(...lintClauseLocal(entry.id, i, clause)));
  out.push(...ruleValPlaceholder(entry));
  out.push(...ruleActionNames(entry));
  return out;
}

/** Run every v1 local rule over a whole overlay corpus (the package-time mode's local half — composes with the global rule below). */
export function lintCorpusLocal(entries: readonly OverlayEntry[]): LintFinding[] {
  return entries.flatMap(lintEntryLocal);
}

// ── the global rule: dead status read ─────────────────────────────────────────────────

/**
 * Statuses applied directly by **hardcoded engine code** via a structural `status_add`
 * resolution step, OUTSIDE the effects DSL's own `add_status`/`remove_status` atom
 * interpreters (`interpret.ts`'s `interpretEffect` is the ONE place the DSL atoms themselves
 * turn into a `status_add`/`status_remove` step) — the **third source of status-name truth**
 * (`INTRINSIC_STATUS_NAMES` + pack-entry ids are the other two). A pack
 * clause reading `has_status`/`remove_status` for one of these names is checking something
 * genuinely live even though no entry defines it and no authored clause ever `add_status`'s
 * it — the engine applies it itself, directly, on a resolution path an author can't reach.
 *
 * **Traced by sweeping every non-DSL `status_add`/direct-`.statuses`-construction site**:
 * `applyResolved.ts` (Destroyed/Vaporized, mech destruction), `reducer.ts`'s door
 * toggle (Open), `tables.ts`'s cascade/pilot-death tables (Cascading/Dead), `pilotBridge.ts`'s
 * eject/dismount (Unpiloted), `jockey.ts` (Riding/Ridden/Jockeying/Jockeyed), `disengage.ts`
 * (Disengaged), `engageCompCon.ts` (COMP/CON Engaged), `grapple.ts`/`refereeContestAction.ts`
 * (Grapple Dominance). Names ALSO covered by {@link INTRINSIC_STATUS_NAMES} (Engaged, Exposed,
 * Stunned, Down and Out, Hidden, Shut Down, Impaired, Slowed, Braced — every one of which is
 * ALSO hardcoded-applied somewhere in this same sweep) are deliberately omitted here; they
 * already clear source one, so listing them again would just be dead weight.
 *
 * **Hand-maintained, same maintenance class as {@link INTRINSIC_STATUS_NAMES}/
 * `FAMILY_BOUND_ROLES`** — a new hardcoded `status_add` site minting a NEW name must extend
 * this list, or {@link lintCorpusGlobal} will false-positive the first pack clause that
 * legitimately reads it. **None of these names are read via `has_status`/`remove_status`
 * anywhere in the shipped corpus** (verified by sweeping the real sidecar) — this table is a
 * forward-looking correctness requirement the rule needs to be right, not a fix for an existing
 * false positive. A follow-up sweep is legitimate if this list ever drifts.
 */
export const HARDCODED_ENGINE_STATUS_NAMES: readonly string[] = [
  "Black Thumb Rodeo",
  "Destroyed",
  "Vaporized",
  "Open",
  "Dead",
  "Cascading",
  "Unpiloted",
  "Riding",
  "Ridden",
  "Jockeying",
  "Jockeyed",
  "Disengaged",
  "COMP/CON Engaged",
  "Grapple Dominance",
];

const INTRINSIC_STATUS_LOOKUP = new Set(INTRINSIC_STATUS_NAMES.map((n) => n.trim().toLowerCase()));
const HARDCODED_ENGINE_STATUS_LOOKUP = new Set(HARDCODED_ENGINE_STATUS_NAMES.map((n) => n.trim().toLowerCase()));

/** Collect every `has_status`(predicate)/`remove_status`(atom) status-name READ site in one clause — the two ops the v1 catalog's global rule names. */
function collectStatusReadSites(clause: EffectEntry): { status: string; path: string }[] {
  const out: { status: string; path: string }[] = [];
  const cond = clauseCondition(clause);
  if (cond) {
    walkPredicateTree(cond, "condition", (node, path) => {
      if (node.op === "has_status") out.push({ status: node.status, path: `${path}.status` });
    });
  }
  walkAtoms(clauseEffectAtoms(clause), "effect", {
    atom: (node, path) => {
      if (node.op === "remove_status") out.push({ status: node.status, path: `${path}.status` });
    },
    pred: (node, path) => {
      if (node.op === "has_status") out.push({ status: node.status, path: `${path}.status` });
    },
  });
  return out;
}

/** Collect every status name an `add_status` (or a rodeo atom's or a drone deploy's planted statuses) lands anywhere in a clause (recursive — nested control-flow bodies included via {@link walkAtoms}). */
function collectAddStatusNames(clause: EffectEntry): string[] {
  const out: string[] = [];
  walkAtoms(clauseEffectAtoms(clause), "effect", {
    atom: (node) => {
      if (node.op === "add_status") out.push(node.status);
      if (node.op === "begin_rodeo") out.push(...(node.rider_statuses ?? []).map((r) => r.status));
      if (node.op === "end_rodeo" && node.lockout) out.push(node.lockout.status);
      if (node.op === "deploy_object") out.push(...(node.statuses ?? []).map((s) => (typeof s === "string" ? s : s.status)));
    },
    pred: () => {},
  });
  return out;
}

// ── the global rule: a `sourceOverride` grant read as a combatant ─────────────────────

/** Case-/whitespace-insensitive status-name key — the same normalization `loader.ts`'s `statusOverlayEntry` applies. */
function statusKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * **`source-override-misread`** — pairs every `add_status` atom that sets
 * `AddStatusAtom.sourceOverride` against the four runtime consumers that read
 * `StatusInstance.source` as a COMBATANT id. The override stamps an item id (e.g.
 * `"ms_type_i_flight_system"`) there instead, so each consumer silently misreads it:
 *
 *   1. **`durationBus.ts`'s `expiresAt`** — a `turn` expiry with `who: "source"` matches the
 *      event's combatant against `source`. An item id is never a combatant, so the instance
 *      never expires on a turn edge (only the scene-end sweep clears it). Checked on the atom's
 *      own `until`.
 *   2. **`remove_status_from_source`** (`interpret.ts`) — removes instances whose `source` is the
 *      acting combatant, so it can never remove an overridden grant. Checked corpus-wide: any
 *      entry's `remove_status_from_source` of the same status name.
 *   3. **`gatherStatusHandlers`' `carrierSource`** (`loader.ts`) — threads `source` onto every
 *      triggered handler of the status's own overlay entry as the CASTER (`has_status` with
 *      `source: "carrier"`, the move/to-hit reaction offers' decider). Checked as: the status's
 *      defining entry carries any `triggered` clause.
 *   4. **`has_status` with a combatant-typed `source`** (`predicate.ts`'s `statusSourceId`) —
 *      `source: "carrier"` resolves to `carrierSource` and `source: { actor }` to a combatant id,
 *      so neither ever equals an item id and the predicate is always false for this grant.
 *      Checked corpus-wide: any clause `condition` (a modifier's `when`), `if` `cond`, or
 *      `for_each`-family `in.where` reading the same status name that way. `source: { id }` is
 *      the intended pairing and is never flagged.
 *
 * Needs the corpus (checks 2–4 read other entries), so it lives in the global pass. Each
 * collision yields one finding on the overriding atom (the authoring choice that created it),
 * plus one on each consumer site in a DIFFERENT entry — so `lvtt pack build`, which keeps only
 * the building pack's own findings, still rejects a pack that adds a consumer against an
 * override living in the seed corpus. (A consumer against another non-seed pack's override is
 * not seen at all: each pack builds against the seed plus itself.) Type-I Flight (`Flying`: a
 * modifier-only entry, `until` keyed to the target, no source-scoped remover or combatant-source
 * `has_status` reader) is the one shipped user and is clean.
 */
type SourceConsumerKind = "remove_status_from_source" | "triggered" | "has_status";
/** `idReads`: the `{ id }` sources the same clause also reads this status with — a grant under one of them is covered. */
type SourceConsumerSite = { consumer: SourceConsumerKind; entry: string; clause: number; locator: string; idReads?: ReadonlySet<string> };

const SOURCE_CONSUMER_WHY: Record<SourceConsumerKind, (status: string) => string> = {
  remove_status_from_source: (status) =>
    `some entry removes "${status}" with \`remove_status_from_source\`, which matches \`source\` against the acting combatant and can never remove this grant.`,
  triggered: (status) =>
    `"${status}"'s own entry carries a triggered clause, and gatherStatusHandlers threads \`source\` onto its handlers as the caster (\`carrierSource\`).`,
  has_status: (status) =>
    `some entry reads "${status}" with a \`has_status\` whose \`source\` is \`"carrier"\` or \`{ actor }\`, which resolves to a combatant id and never matches this grant (use \`source: { id }\`).`,
};

function ruleSourceOverrideMisread(entries: readonly OverlayEntry[]): LintFinding[] {
  const consumers = new Map<string, SourceConsumerSite[]>();
  const addConsumer = (status: string, site: SourceConsumerSite): void => {
    const key = statusKey(status);
    const list = consumers.get(key);
    if (list) list.push(site);
    else consumers.set(key, [site]);
  };
  for (const entry of entries) {
    entry.effects.forEach((clause, clauseIdx) => {
      if (clause.kind === "triggered") addConsumer(entry.id, { consumer: "triggered", entry: entry.id, clause: clauseIdx, locator: "on" });
      // A combatant-source read OR'd with an `{ id }` read of the same status covers that id's
      // grant (Black Thumb's "not self-inflicted" also reads Expanded Compartment's own penalty).
      const idReads = new Map<string, Set<string>>();
      const collectIdReads = (node: Predicate): void => {
        if (node.op !== "has_status" || node.source === undefined || node.source === "carrier" || !("id" in node.source)) return;
        const key = statusKey(node.status);
        idReads.set(key, (idReads.get(key) ?? new Set()).add(node.source.id));
      };
      const visitPred = (node: Predicate, path: string): void => {
        if (node.op === "has_status" && node.source !== undefined && (node.source === "carrier" || "actor" in node.source)) {
          const paired = idReads.get(statusKey(node.status));
          addConsumer(node.status, { consumer: "has_status", entry: entry.id, clause: clauseIdx, locator: `${path}.source`, ...(paired ? { idReads: paired } : {}) });
        }
      };
      const cond = clauseCondition(clause);
      for (const visit of [collectIdReads, visitPred]) {
        if (cond) walkPredicateTree(cond, "condition", visit);
        walkAtoms(clauseEffectAtoms(clause), "effect", {
          atom: (node, path) => {
            if (visit === visitPred && node.op === "remove_status_from_source") addConsumer(node.status, { consumer: "remove_status_from_source", entry: entry.id, clause: clauseIdx, locator: path });
          },
          pred: visit,
        });
      }
    });
  }

  const out: LintFinding[] = [];
  for (const entry of entries) {
    entry.effects.forEach((clauseDef, clauseIdx) => {
      walkAtoms(clauseEffectAtoms(clauseDef), "effect", {
        atom: (node, path) => {
          if (node.op !== "add_status" || node.sourceOverride === undefined) return;
          const grant = `\`add_status\` of "${node.status}" at ${entry.id} ${path} sets \`sourceOverride: "${node.sourceOverride}"\`, which stamps an item id into \`StatusInstance.source\``;
          const push = (at: { entry: string; clause: number; locator: string }, why: string): void => {
            out.push(
              finding({
                ...at,
                rule: "source-override-misread",
                severity: "error",
                message: `${grant} — but ${why} Drop the override or change the consumer.`,
              }),
            );
          };
          if (node.until?.kind === "turn" && node.until.who === "source") {
            push({ entry: entry.id, clause: clauseIdx, locator: `${path}.until.who` }, `its own \`until\` is a \`who: "source"\` turn expiry, which durationBus.ts matches against a combatant id, so it never expires on a turn edge.`);
          }
          const sourceOverride = node.sourceOverride;
          const sites = (consumers.get(statusKey(node.status)) ?? []).filter((site) => !site.idReads?.has(sourceOverride));
          const kinds = new Set(sites.map((s) => s.consumer));
          for (const consumer of kinds) push({ entry: entry.id, clause: clauseIdx, locator: `${path}.sourceOverride` }, SOURCE_CONSUMER_WHY[consumer](node.status));
          for (const site of sites) {
            if (site.entry !== entry.id) push({ entry: site.entry, clause: site.clause, locator: site.locator }, SOURCE_CONSUMER_WHY[site.consumer](node.status));
          }
        },
        pred: () => {},
      });
    });
  }
  return out;
}

/**
 * The v1 catalog's **global** rule: a status named in `has_status`/`remove_status` that is (a)
 * not intrinsic, (b) not itself the id of any entry in the corpus, (c) never `add_status`'d by
 * any clause anywhere in the corpus, and (d) not one of the {@link HARDCODED_ENGINE_STATUS_NAMES}
 * is a guaranteed-false predicate — `error`. Needs the **whole corpus** — deliberately NOT
 * wired into {@link lintEntryLocal}/the fast per-entry mode: running it there would false-
 * error an authoring agent against a *sibling* agent's in-flight work (the status it's checking
 * for might land two minutes from now, from an entry this run can't see). Package-time only
 * — `lvtt pack build` (`cli/src/pack-cli.ts`) is the one caller.
 *
 * **Measurement that disqualified the naive variant (the load-bearing number,
 * `pack-content-validation-lint.md`):** the naive rule ("a status is `add_status`'d but no
 * entry defines its mechanics") produces FOUR false positives on the real corpus — Soft Cover /
 * Prowling / Juggernaut Charged / Skirmisher's Cover, all legitimate self-minted bookkeeping
 * markers whose "definition" is the hardcoded engine read that consumes them, not a separate
 * overlay entry. **This rule scores zero**, and not by coincidence: it never asks whether an
 * `add_status`'d name has its own defining entry — it asks whether a *read* status is reachable
 * through any of the four sources above, and all four of those markers clear source (c) (they
 * ARE `add_status`'d somewhere in the corpus) regardless of whether a defining entry exists.
 * `lint.test.ts` carries all four as explicit named regression fixtures so this can't quietly
 * regress back to the naive, false-positive-producing shape.
 */
export function lintCorpusGlobal(entries: readonly OverlayEntry[]): LintFinding[] {
  const entryIds = new Set(entries.map((e) => e.id));
  const addStatusNames = new Set<string>();
  for (const entry of entries) {
    for (const clause of entry.effects) {
      for (const name of collectAddStatusNames(clause)) addStatusNames.add(name);
    }
  }

  const out: LintFinding[] = [];
  for (const entry of entries) {
    entry.effects.forEach((clause, clauseIdx) => {
      for (const { status, path } of collectStatusReadSites(clause)) {
        const intrinsic = INTRINSIC_STATUS_LOOKUP.has(status.trim().toLowerCase());
        const entryDefined = entryIds.has(status);
        const everAdded = addStatusNames.has(status);
        const hardcodedEngine = HARDCODED_ENGINE_STATUS_LOOKUP.has(status.trim().toLowerCase());
        if (intrinsic || entryDefined || everAdded || hardcodedEngine) continue;
        out.push(
          finding({
            entry: entry.id,
            clause: clauseIdx,
            rule: "dead-status-read",
            severity: "error",
            locator: path,
            message: `Status "${status}" at ${path} is not intrinsic, not defined by any entry in the corpus, never \`add_status\`'d anywhere, and is not a known hardcoded-engine-applied status — this predicate/atom reads a status that can never be true. File a vocabulary-demand, fix the status name, or author the entry that defines it.`,
          }),
        );
      }
    });
  }
  out.push(...ruleSourceOverrideMisread(entries));
  return out;
}
